import { useEffect, useState } from "react";

type ExitTransition<T> = {
  /** What to render: the live value, or the last one while it exits. */
  readonly displayed: T | null;
  /** True while `displayed` is a value that has already been removed. */
  readonly isExiting: boolean;
};

/**
 * Keeps a removed value on screen for `exitMs`, so the element showing it can
 * play an exit animation instead of vanishing the instant its source turns
 * `null`. A new value arriving mid-exit replaces it at once.
 */
export function useExitTransition<T>(
  value: T | null,
  exitMs: number,
): ExitTransition<T> {
  const [previous, setPrevious] = useState(value);
  const [exiting, setExiting] = useState<T | null>(null);

  // Adjusted during render rather than in an effect, so the frame where the
  // value turns `null` already renders the exiting one — an effect would
  // commit one empty frame first, and the exit would start from nothing.
  if (value !== previous) {
    setPrevious(value);
    setExiting(value === null ? previous : null);
  }

  useEffect(() => {
    if (exiting === null) return;
    const timer = window.setTimeout(() => setExiting(null), exitMs);
    return () => window.clearTimeout(timer);
  }, [exiting, exitMs]);

  if (value !== null) {
    return { displayed: value, isExiting: false };
  }
  return { displayed: exiting, isExiting: exiting !== null };
}
